/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { defineMessages } from 'react-intl'
import { capitalize } from 'lodash'

import { FormattedMessage } from '@modules/i18n'
import { ApiAccessOptions } from '@modules/role-assignments-components/ApiAccessSelector/types'

import type {
  DeploymentRoleLabel,
  ElasticsearchRoleLabel,
  ObservabilityRoleLabel,
  OrganizationRoleLabel,
  SecurityRoleLabel,
  ResourceType,
  RoleLabel,
  CustomRoleBadgeLabelType,
  CloudConnectedRoleLabelType,
} from './types'
import type { MessageDescriptor } from 'react-intl'

const organizationRoleLabels = defineMessages<OrganizationRoleLabel>({
  'organization-admin': {
    id: 'roles-assignments.organization-owner',
    defaultMessage: 'Organization owner',
  },
  'billing-admin': {
    id: 'roles-assignments.billing-admin',
    defaultMessage: 'Billing admin',
  },
})

export const organizationRoleDescriptions = defineMessages<OrganizationRoleLabel>({
  'organization-admin': {
    id: 'roles-assignments.organization-owner-description',
    defaultMessage:
      'Highest role within the organization. Can manage members, access all features and all resources.',
  },
  'billing-admin': {
    id: 'roles-assignments.billing-admin-description',
    defaultMessage: 'Access to all invoices and payment methods. Can make subscription changes.',
  },
})

export const deploymentRoleLabels = defineMessages<DeploymentRoleLabel>({
  admin: {
    id: 'roles-assignments.admin',
    defaultMessage: 'Admin',
  },
  editor: {
    id: 'roles-assignments.editor',
    defaultMessage: 'Editor',
  },
  viewer: {
    id: 'roles-assignments.viewer',
    defaultMessage: 'Viewer',
  },
})

export const elasticsearchRoleLabels = defineMessages<ElasticsearchRoleLabel>({
  admin: {
    id: 'roles-assignments.admin',
    defaultMessage: 'Admin',
  },
  developer: {
    id: 'roles-assignments.developer',
    defaultMessage: 'Developer',
  },
  viewer: {
    id: 'roles-assignments.viewer',
    defaultMessage: 'Viewer',
  },
})

export const observabilityRoleLabels = defineMessages<ObservabilityRoleLabel>({
  admin: {
    id: 'roles-assignments.admin',
    defaultMessage: 'Admin',
  },
  editor: {
    id: 'roles-assignments.editor',
    defaultMessage: 'Editor',
  },
  viewer: {
    id: 'roles-assignments.viewer',
    defaultMessage: 'Viewer',
  },
})

export const securityRoleLabels = defineMessages<SecurityRoleLabel>({
  admin: {
    id: 'roles-assignments.admin',
    defaultMessage: 'Admin',
  },
  editor: {
    id: 'roles-assignments.editor',
    defaultMessage: 'Editor',
  },
  viewer: {
    id: 'roles-assignments.viewer',
    defaultMessage: 'Viewer',
  },
  tier1Analyst: {
    id: 'roles-assignments.tier-1-analyst',
    defaultMessage: 'Tier 1 analyst',
  },
  tier2Analyst: {
    id: 'roles-assignments.tier-2-analyst',
    defaultMessage: 'Tier 2 analyst',
  },
  tier3Analyst: {
    id: 'roles-assignments.tier-3-analyst',
    defaultMessage: 'Tier 3 analyst',
  },
  threatIntelligenceAnalyst: {
    id: 'role-badges.threat-intelligence-analyst',
    defaultMessage: 'Threat intelligence analyst',
  },
  ruleAuthor: {
    id: 'role-badges.rule-author',
    defaultMessage: 'Rule author',
  },
  socManager: {
    id: 'role-badges.soc-manager',
    defaultMessage: 'SOC manager',
  },
  endpointOperationsAnalyst: {
    id: 'role-badges.endpoint-operations-analyst',
    defaultMessage: 'Endpoint operations analyst',
  },
  platformEngineer: {
    id: 'role-badges.platform-engineer',
    defaultMessage: 'Platform engineer',
  },
  detectionsAdmin: {
    id: 'role-badges.detections-admin',
    defaultMessage: 'Detections admin',
  },
  endpointPolicyManager: {
    id: 'role-badges.endpoint-policy-manager',
    defaultMessage: 'Endpoint policy manager',
  },
  searchAiLakeAnalyst: {
    id: 'role-badges.search-ai-lake-analyst',
    defaultMessage: 'Analyst',
  },
  searchAiLakeSocManager: {
    id: 'role-badges.search-ai-lake-soc-manager',
    defaultMessage: 'AI SOC Engine manager',
  },
  aiSocEngineAnalyst: {
    id: 'role-badges.ai-soc-engine-analyst',
    defaultMessage: 'Analyst',
  },
  aiSocEngineSocManager: {
    id: 'role-badges.ai-soc-engine-soc-manager',
    defaultMessage: 'AI SOC Engine manager',
  },
})

const cloudAPIAccessLabels = defineMessages<
  ApiAccessOptions | 'cloud_console' | 'cloud_elasticsearch_kibana_console'
>({
  cloud_api: {
    id: 'roles-assignments.cloud-api.access.label',
    defaultMessage: 'Cloud API',
  },
  cloud_elasticsearch_kibana_api: {
    id: 'roles-assignments.cloud-elasticsearch-kibana-api.access.label',
    defaultMessage: 'Cloud, Elasticsearch, and Kibana API',
  },
  cloud_console: {
    id: 'roles-assignments.cloud-console.access.label',
    defaultMessage: 'Cloud Console',
  },
  cloud_elasticsearch_kibana_console: {
    id: 'roles-assignments.cloud-console-elasticsearch-kibana.access.label',
    defaultMessage: 'Cloud Console, Elasticsearch, and Kibana',
  },
})

export function getCloudAPIAccessLabel({
  apiAccess,
  isApiKey,
}: {
  apiAccess: ApiAccessOptions
  isApiKey: boolean
}) {
  switch (apiAccess) {
    case ApiAccessOptions.CloudApi:
      return isApiKey ? cloudAPIAccessLabels.cloud_api : cloudAPIAccessLabels.cloud_console
    case ApiAccessOptions.CloudElasticsearchKibanaApi:
      return isApiKey
        ? cloudAPIAccessLabels.cloud_elasticsearch_kibana_api
        : cloudAPIAccessLabels.cloud_elasticsearch_kibana_console
    default:
      return undefined
  }
}

export const ccmClusterRoleLabels = defineMessages<CloudConnectedRoleLabelType>({
  admin: {
    id: 'roles-assignments.ccm.admin',
    defaultMessage: 'Admin',
  },
  viewer: {
    id: 'roles-assignments.ccm.viewer',
    defaultMessage: 'Viewer',
  },
})

export const predefinedLabels: Record<ResourceType, Record<string, MessageDescriptor>> = {
  deployment: deploymentRoleLabels,
  elasticsearch: elasticsearchRoleLabels,
  observability: observabilityRoleLabels,
  security: securityRoleLabels,
  'cloud-connected': ccmClusterRoleLabels,
}

const customRoleBadgeLabels = defineMessages<CustomRoleBadgeLabelType>({
  customRole: {
    id: 'role-badges.custom',
    defaultMessage: 'Custom role',
  },
})

const cloudConnectedRoleBadgeLabels = defineMessages<CloudConnectedRoleLabelType>({
  admin: {
    id: 'roles-assignments.admin',
    defaultMessage: 'Admin',
  },
  viewer: {
    id: 'roles-assignments.viewer',
    defaultMessage: 'Viewer',
  },
})

export const roleBadgeLabels = defineMessages<RoleLabel>({
  ...organizationRoleLabels,
  ...deploymentRoleLabels,
  ...elasticsearchRoleLabels,
  ...observabilityRoleLabels,
  ...securityRoleLabels,
  ...customRoleBadgeLabels,
  ...cloudConnectedRoleBadgeLabels,
})

export const deploymentRoleDescriptions = defineMessages<DeploymentRoleLabel>({
  admin: {
    id: 'roles-assignments.deployment.admin.description',
    defaultMessage:
      'Can create, edit, view, delete data and manage the configuration of a hosted deployment.',
  },
  editor: {
    id: 'roles-assignments.deployment.editor.description',
    defaultMessage: 'Can create, edit, view and delete data.',
  },
  viewer: {
    id: 'roles-assignments.deployment.viewer.description',
    defaultMessage: 'Can view and operate on existing data.',
  },
})

export const elasticsearchRoleDescriptions = defineMessages<ElasticsearchRoleLabel>({
  admin: {
    id: 'roles-assignments.project.elasticsearch.admin.description',
    defaultMessage:
      '<strong>Cloud:</strong> Configure project settings, including security.{br}<strong>Project:</strong> Full superuser access to all data, features, and configurations.',
  },
  developer: {
    id: 'roles-assignments.project.elasticsearch.developer.description',
    defaultMessage:
      '<strong>Cloud:</strong> Manage project settings.{br}<strong>Project:</strong> Create and manage API keys, indices, data streams, connectors, and visualizations.',
  },
  viewer: {
    id: 'roles-assignments.project.elasticsearch.viewer.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Read-only access to project data and features.',
  },
})

export const observabilityRoleDescriptions = defineMessages<ObservabilityRoleLabel>({
  admin: {
    id: 'roles-assignments.project.observability.admin.description',
    defaultMessage:
      '<strong>Cloud:</strong> Configure project settings, including security.{br}<strong>Project:</strong> Full superuser access to all data, features, and configurations.',
  },
  editor: {
    id: 'roles-assignments.project.observability.editor.description',
    defaultMessage:
      '<strong>Cloud:</strong> Manage project settings.{br}<strong>Project:</strong> Read-only access to data indices. Full access to all project features.',
  },
  viewer: {
    id: 'roles-assignments.project.observability.viewer.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Read-only access to project data and features.',
  },
})

export const securityRoleDescriptions = defineMessages<SecurityRoleLabel>({
  admin: {
    id: 'roles-assignments.project.security.admin.description',
    defaultMessage:
      '<strong>Cloud:</strong> Configure project settings, including security.{br}<strong>Project:</strong> Full superuser access to all data, features, and configurations.',
  },
  editor: {
    id: 'roles-assignments.project.security.editor.description',
    defaultMessage:
      '<strong>Cloud:</strong> Manage project settings.{br}<strong>Project:</strong> Read-only access to data indices. Full access to all project features.',
  },
  viewer: {
    id: 'roles-assignments.project.security.viewer.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Read-only access to project data and features.',
  },
  tier1Analyst: {
    id: 'roles-assignments.project.security.tier-1-analyst.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Ideal for initial alert triage. General read access, can create dashboards and visualizations.',
  },
  tier2Analyst: {
    id: 'roles-assignments.project.security.tier-2-analyst.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Ideal for alert triage and beginning the investigation process. Can create Cases.',
  },
  tier3Analyst: {
    id: 'roles-assignments.project.security.tier-3-analyst.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Deeper investigation capabilities. Access to rules, lists, cases, Osquery, and response actions.',
  },
  threatIntelligenceAnalyst: {
    id: 'roles-assignments.project.security.threat-intelligence-analyst.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Access to alerts, investigation tools, and intelligence pages.',
  },
  ruleAuthor: {
    id: 'roles-assignments.project.security.rule-author.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Full management access to platform detection content and automation capabilities.',
  },
  socManager: {
    id: 'roles-assignments.project.security.soc-manager.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Access to alerts, cases, investigation tools, endpoint policy management, and response actions.',
  },
  endpointOperationsAnalyst: {
    id: 'roles-assignments.project.security.endpoint-operations-analyst.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Access to endpoint response actions. Can manage endpoint policies, Fleet, and integrations.',
  },
  platformEngineer: {
    id: 'roles-assignments.project.security.platform-engineer.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Access to Fleet, integrations, endpoints, and detection content.',
  },
  detectionsAdmin: {
    id: 'roles-assignments.project.security.detections-admin.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Full management access to platform detection content and automation capabilities.',
  },
  endpointPolicyManager: {
    id: 'roles-assignments.project.security.endpoint-policy-manager.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Access to endpoint policy management and related artifacts. Can manage Fleet and integrations.',
  },
  searchAiLakeAnalyst: {
    id: 'roles-assignments.project.security.search-ai-lake-analyst.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Access to alerts, investigation tools, and AI SOC Engine pages.',
  },
  searchAiLakeSocManager: {
    id: 'roles-assignments.project.security.search-ai-lake-soc-manager.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Full management access to AI SOC Engine content and automation capabilities.',
  },
  aiSocEngineAnalyst: {
    id: 'roles-assignments.project.security.ai-soc-engine-analyst.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Access to alerts, investigation tools, and AI SOC Engine pages.',
  },
  aiSocEngineSocManager: {
    id: 'roles-assignments.project.security.ai-soc-engine-soc-manager.description',
    defaultMessage:
      '<strong>Cloud:</strong> Read-only access to project details.{br}<strong>Project:</strong> Full management access to AI SOC Engine content and automation capabilities.',
  },
})

export const ccmClusterRoleDescriptions = defineMessages<CloudConnectedRoleLabelType>({
  admin: {
    id: 'roles-assignments.ccm.admin.description',
    defaultMessage: 'Can connect clusters and edit settings',
  },
  viewer: {
    id: 'roles-assignments.ccm.viewer.description',
    defaultMessage: 'Can view connected clusters',
  },
})

export function ResourceTypeDisplay({
  resourceType,
  plural,
  capitalize,
}: {
  resourceType: ResourceType
  plural?: boolean
  capitalize?: boolean
}) {
  const getDeploymentDisplay = () => {
    const messages = defineMessages({
      normal: {
        id: 'deployments.long.type',
        defaultMessage: 'hosted {count, plural, one {deployment} other {deployments}}',
        values: { count: plural ? 2 : 1 },
      },
      capitalized: {
        id: 'deployments.long.type.capitalized',
        defaultMessage: 'Hosted {count, plural, one {deployment} other {deployments}}',
        values: { count: plural ? 2 : 1 },
      },
    })

    return capitalize ? { ...messages.capitalized } : { ...messages.normal }
  }

  const resourceTypes: Record<ResourceType, MessageDescriptor> = defineMessages({
    elasticsearch: {
      id: 'projects.long.type.elasticsearch',
      defaultMessage: 'Elasticsearch serverless {count, plural, one {project} other {projects}}',
      values: { count: plural ? 2 : 1 },
    },
    observability: {
      id: 'projects.long.type.observability',
      defaultMessage: 'Observability serverless {count, plural, one {project} other {projects}}',
      values: { count: plural ? 2 : 1 },
    },
    security: {
      id: 'projects.long.type.security',
      defaultMessage: 'Security serverless {count, plural, one {project} other {projects}}',
      values: { count: plural ? 2 : 1 },
    },
    'cloud-connected': {
      id: 'projects.long.type.cloud-connected',
      defaultMessage: 'Cloud Connected {count, plural, one {cluster} other {clusters}}',
      values: { count: plural ? 2 : 1 },
    },
    deployment: getDeploymentDisplay(),
  })

  return <FormattedMessage {...resourceTypes[resourceType]} />
}

export function ApiAccessSelectorTitle({
  isApiKey,
  isOrgOwner,
}: {
  isApiKey: boolean
  isOrgOwner: boolean
}) {
  if (isApiKey) {
    return <FormattedMessage id='api-access-selector.title.api-key' defaultMessage='API Access' />
  }

  if (isOrgOwner) {
    return (
      <FormattedMessage
        id='api-access-selector.title.org-owner'
        defaultMessage='Serverless project access'
      />
    )
  }

  return <FormattedMessage id='api-access-selector.title.generic' defaultMessage='Access' />
}

export function CloudApiAccessDescription({
  resourceType,
  isApiKey,
  isSpecificResource,
}: {
  resourceType?: ResourceType
  isApiKey: boolean
  isSpecificResource: boolean
}) {
  if (resourceType) {
    if (isSpecificResource) {
      return isApiKey ? (
        <FormattedMessage
          id='roles-assignments.api.access.selector.cloud-api-description-specific-resource-api-key'
          defaultMessage='Grant role access to this project through the Elastic Cloud API.'
        />
      ) : (
        <FormattedMessage
          id='roles-assignments.api.access.selector.cloud-api-description-specific-resource'
          defaultMessage='Grant role access to this project in the Cloud Console only.'
        />
      )
    }

    return isApiKey ? (
      <FormattedMessage
        id='roles-assignments.api.access.selector.cloud-api-description-resource-api-key'
        defaultMessage='Grant role access to all {projectType} projects through the Elastic Cloud API.'
        values={{
          projectType: capitalize(resourceType),
        }}
      />
    ) : (
      <FormattedMessage
        id='roles-assignments.api.access.selector.cloud-api-description-resource'
        defaultMessage='Grant role access to all {projectType} projects in the Cloud Console only.'
        values={{
          projectType: capitalize(resourceType),
        }}
      />
    )
  }

  if (isApiKey) {
    return (
      <FormattedMessage
        id='roles-assignments.api.access.selector.cloud-api-description'
        defaultMessage='Grant Admin-level access to the Elastic Cloud API for your organization.'
      />
    )
  }

  return (
    <FormattedMessage
      id='roles-assignments.api.access.selector.cloud-console-description'
      defaultMessage='Grant access to manage projects in the Cloud Console. No access to Kibana or Elasticsearch for projects.'
    />
  )
}

export function CloudEsKibanaApiAccessDescription({
  resourceType,
  isApiKey,
  isSpecificResource,
}: {
  resourceType?: ResourceType
  isApiKey: boolean
  isSpecificResource: boolean
}) {
  if (resourceType) {
    if (isSpecificResource) {
      return isApiKey ? (
        <FormattedMessage
          id='roles-assignments.api.access.selector.cloud-es-kibana-api-description-specific-resource-api-key'
          defaultMessage="Grant role access to this project through the Elastic Cloud API, plus the project's Elasticsearch and Kibana API endpoints."
        />
      ) : (
        <FormattedMessage
          id='roles-assignments.api.access.selector.cloud-es-kibana-api-console-description-specific-resource'
          defaultMessage='Grant role access to this project in the Cloud Console, and project-level access to Kibana and Elasticsearch.'
        />
      )
    }

    return isApiKey ? (
      <FormattedMessage
        id='roles-assignments.api.access.selector.cloud-es-kibana-api-description-resource-api-key'
        defaultMessage="Grant role access to all {projectType} projects through the Elastic Cloud API, plus each project's Elasticsearch and Kibana API endpoints."
        values={{
          projectType: capitalize(resourceType),
        }}
      />
    ) : (
      <FormattedMessage
        id='roles-assignments.api.access.selector.cloud-es-kibana-api-console-description-resource'
        defaultMessage='Grant role access to all {projectType} projects in the Cloud Console, and project-level access to Kibana and Elasticsearch.'
        values={{
          projectType: capitalize(resourceType),
        }}
      />
    )
  }

  if (isApiKey) {
    return (
      <FormattedMessage
        id='roles-assignments.api.access.selector.cloud-es-kibana-api-description'
        defaultMessage='Grant Admin-level access to the Elastic Cloud API, plus Elasticsearch and Kibana API endpoints for all Serverless projects in your organization.'
      />
    )
  }

  return (
    <FormattedMessage
      id='roles-assignments.api.access.selector.cloud-es-kibana-api-console-description'
      defaultMessage='Grant access to manage projects in the Cloud Console, and project-level access to Kibana and Elasticsearch.'
    />
  )
}
